import { Shape } from "../../../../types";

export interface HistoryState {
  shapes: Shape[];
  selectedIds: string[];
}

const MAX_HISTORY_SIZE = 100;

export class HistoryManager {
  private undoStack: HistoryState[] = [];
  private redoStack: HistoryState[] = [];
  private currentState: HistoryState;
  private isPerformingUndoRedo = false;
  private isRecording = true;

  constructor(initialState: HistoryState) {
    this.currentState = initialState;
  }

  public pushState(newState: HistoryState) {
    if (this.isPerformingUndoRedo || !this.isRecording) {
      return;
    }

    // Skip no-op: same shapes reference means nothing changed
    if (newState.shapes === this.currentState.shapes) {
      return;
    }

    this.undoStack.push({ ...this.currentState });
    if (this.undoStack.length > MAX_HISTORY_SIZE) {
      this.undoStack.shift();
    }
    this.currentState = newState;
    this.redoStack = [];
  }

  public undo(): HistoryState | null {
    if (this.undoStack.length === 0) {
      return null;
    }

    this.isPerformingUndoRedo = true;
    this.redoStack.push({ ...this.currentState });
    this.currentState = this.undoStack.pop()!;
    this.isPerformingUndoRedo = false;

    return { ...this.currentState };
  }

  public redo(): HistoryState | null {
    if (this.redoStack.length === 0) {
      return null;
    }

    this.isPerformingUndoRedo = true;
    this.undoStack.push({ ...this.currentState });
    this.currentState = this.redoStack.pop()!;
    this.isPerformingUndoRedo = false;

    return { ...this.currentState };
  }

  public getCurrentState(): HistoryState {
    return { ...this.currentState };
  }

  public canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  public canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Sync the base state with loaded shapes before the first user action.
   *  Only updates if the undo stack is empty and current state has no shapes. */
  public ensureBaseState(shapes: Shape[], selectedIds: string[]): void {
    if (this.undoStack.length === 0 && this.currentState.shapes.length === 0 && shapes.length > 0) {
      this.currentState = { shapes, selectedIds };
    }
  }

  public disableRecording(): void {
    this.isRecording = false;
  }

  public enableRecording(): void {
    this.isRecording = true;
  }
}
